import { X } from 'lucide-react'
import { type ComponentProps } from 'react'

import { AUDIT_PERMISSIONS } from '@/lib/permissions'
import { usePermissions } from '@/lib/use-permissions'
import { Button } from '@/ui/button'
import { buttonVariants } from '@/ui/button-variants'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@/ui/dialog'

import { SubjectAuditLog } from './subject-audit-log'
import { type AuditSubjectType } from './subject-audit-log-queries'

export function SubjectAuditLogDialog({
  entityId,
  subjectType,
  subjectId,
  name,
  open,
  onOpenChange,
  finalFocus,
}: {
  entityId: string
  subjectType: AuditSubjectType
  subjectId: string
  name: string
  open: boolean
  onOpenChange: (open: boolean) => void
  finalFocus?: ComponentProps<typeof DialogContent>['finalFocus']
}) {
  const { can } = usePermissions()
  if (!can(AUDIT_PERMISSIONS.read)) return null
  if (subjectType === 'Policy') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent finalFocus={finalFocus} className="flex max-h-[90vh] max-w-5xl flex-col gap-0 overflow-hidden p-0">
          <div className="border-border flex items-start justify-between gap-4 border-b px-5 py-4">
            <div className="flex flex-col gap-0.5">
              <DialogTitle>Audit log for {name}</DialogTitle>
              <DialogDescription>Changes to this printer policy, newest first.</DialogDescription>
            </div>
            <DialogClose aria-label="Close" className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}>
              <X />
            </DialogClose>
          </div>
          {open && (
            <div className="flex min-h-96 flex-1 flex-col overflow-hidden px-5 pt-3">
              <SubjectAuditLog
                key={`${entityId}:${subjectId}`}
                entityId={entityId}
                subjectType={subjectType}
                subjectId={subjectId}
                urlState={false}
                caption={`Audit entries for ${name}`}
              />
            </div>
          )}
          <DialogFooter className="items-center justify-end gap-4 px-5 py-3">
            <DialogClose className={buttonVariants({ size: 'sm' })}>Done</DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent finalFocus={finalFocus} className="flex max-h-[90dvh] w-[calc(100%-2rem)] max-w-6xl flex-col overflow-y-auto">
        <DialogTitle>Audit log for {name}</DialogTitle>
        <DialogDescription>Recorded changes and activity, newest first.</DialogDescription>
        {open ? (
          <div className="flex min-h-96 flex-1 flex-col overflow-hidden">
            <SubjectAuditLog
              key={`${entityId}:${subjectType}:${subjectId}`}
              entityId={entityId}
              subjectType={subjectType}
              subjectId={subjectId}
              urlState={false}
              caption={`Audit entries for ${name}`}
            />
          </div>
        ) : null}
        <DialogFooter><Button size="sm" variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
